import { COUPON_PATH, ekDomain, ROUTES, WORDPRESS_PATH } from '@/configs/constants';
import { GroupMenu, Menu, Route, Submenu } from '@/types/routes';
import {
    CircleCheckBig,
    CircleGauge,
    MapPinned,
    Settings,
    Users,
    BadgeJapaneseYen,
    Wallet,
    BadgePercentIcon
} from 'lucide-react';
import MotorBikeIcon from '@/components/icon/MotorBikeIcon';
import { ROLE_USER_CODE, RoleUserType } from '@/types/enums';
import { TFunction } from 'i18next';
import { URL_CONFIG } from '@/configs/environment';
import { HelmetIcon } from '@/components/icon/HelmetIcon';
import { CheckListIcon } from '@/components/icon/CheckListIcon';

// #region Title Page
export const getTitlePage = (pathname: string) => {
    if (!pathname) {
        return {
            title: ''
        };
    }

    // title page create new coupon
    if (pathname?.includes(ROUTES.couponSetting.href)) {
        return getTitleAddCouponPage(pathname);
    }

    let pathnameTmp = pathname;
    if (isPathEk(pathname)) {
        pathnameTmp = pathname.replace(`/${ekDomain}`, '');
    }

    const findRoute = Object.values(ROUTES).find((value) => {
        if (value?.pathKey) {
            return pathnameTmp.includes(value.pathKey);
        }
        return pathnameTmp === value.href;
    });

    return findRoute ?? { title: '' };
};

const getTitleAddCouponPage = (pathname: string) => {
    if (pathname.includes(ROUTES.couponCreate.href)) {
        if (pathname.includes(COUPON_PATH.FOREVER)) {
            return {
                title: 'words_title.add_coupon_forever'
            };
        } else if (pathname.includes(COUPON_PATH.ONE_TIME)) {
            return {
                title: 'words_title.add_coupon_one_time'
            };
        } else {
            return {
                title: 'words_title.coupon_create'
            };
        }
    }

    return {
        title: 'words_title.coupon_create'
    };
};
// #rendregion

// #region Breadcrumb
const STATIC_ROUTES_BREADCRUMB: {
    [key: string]: Route[]
} = {
    [ROUTES.dashboard.href]: [ROUTES.dashboard],
    [ROUTES.locationManagement.href]: [ROUTES.locationManagement],
    [ROUTES.optionManagement.href]: [ROUTES.optionManagement]
};

export const getBreadcrumb = (pathname: string): Route[] => {
    if (!pathname) return [];
    switch (pathname) {
        case STATIC_ROUTES_BREADCRUMB.hasOwnProperty(pathname) && pathname:
            return STATIC_ROUTES_BREADCRUMB[pathname];

        default:
            return [];
    }
};
// #endregion

// #region Menu
const hasPermission = (roles: string[], role: string) => roles.includes(role);

export const getMenuList = (
    pathname: string,
    userRole: string
): GroupMenu[] => {
    const isEk = isPathEk(window.location.pathname);

    return [
        {
            groupLabel: '',
            menus: [
                {
                    href: ROUTES.dashboard.href,
                    label: ROUTES.dashboard.menuLabel,
                    active: pathname.includes(ROUTES.dashboard.href),
                    icon: CircleGauge,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.userManagement.href,
                    label: ROUTES.userManagement.menuLabel,
                    active: pathname.includes(ROUTES.userManagement.href),
                    icon: Users,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.bikeManagement.href,
                    label: ROUTES.bikeManagement.menuLabel,
                    active: pathname.includes(ROUTES.bikeManagement.href),
                    icon: MotorBikeIcon,
                    roles: [
                        ROLE_USER_CODE.SYSTEM_ADMIN,
                        ROLE_USER_CODE.BEST_BIKE_ADMIN,
                        ROLE_USER_CODE.FRANCHISE_OWNER_PRM,
                        ROLE_USER_CODE.FRANCHISE_OWNER_STD,
                        ROLE_USER_CODE.HOTEL_BIKE_OWNER,
                        ROLE_USER_CODE.SHARE_BIKE_OWNER
                    ],
                    submenus: []
                },
                {
                    href: ROUTES.locationManagement.href,
                    label: ROUTES.locationManagement.menuLabel,
                    active: pathname.includes(ROUTES.locationManagement.href),
                    icon: MapPinned,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.priceManagement.href,
                    label: ROUTES.priceManagement.menuLabel,
                    active: pathname.includes(ROUTES.priceManagement.href),
                    icon: BadgeJapaneseYen,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.optionManagement.href,
                    label: ROUTES.optionManagement.menuLabel,
                    active: pathname.includes(ROUTES.optionManagement.href),
                    icon: HelmetIcon,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.reservationManagement.href,
                    label: ROUTES.reservationManagement.menuLabel,
                    active: pathname.includes(ROUTES.reservationManagement.href),
                    icon: CheckListIcon,
                    roles: [
                        ROLE_USER_CODE.SYSTEM_ADMIN,
                        ROLE_USER_CODE.BEST_BIKE_ADMIN,
                        ROLE_USER_CODE.FRANCHISE_OWNER_PRM,
                        ROLE_USER_CODE.FRANCHISE_OWNER_STD,
                        ROLE_USER_CODE.HOTEL_BIKE_OWNER,
                        ROLE_USER_CODE.SHARE_BIKE_OWNER
                    ],
                    submenus: []
                },
                {
                    href: '',
                    label: 'words_title.revenue_and_commission',
                    active: false,
                    icon: Wallet,
                    roles: [
                        ROLE_USER_CODE.SYSTEM_ADMIN,
                        ROLE_USER_CODE.BEST_BIKE_ADMIN,
                        ROLE_USER_CODE.FRANCHISE_OWNER_PRM,
                        ROLE_USER_CODE.FRANCHISE_OWNER_STD,
                        ROLE_USER_CODE.HOTEL_BIKE_OWNER,
                        ROLE_USER_CODE.SHARE_BIKE_OWNER
                    ],
                    submenus: [
                        {
                            href: ROUTES.revenueManagement.href,
                            label: ROUTES.revenueManagement.menuLabel,
                            active: pathname.includes(ROUTES.revenueManagement.href),
                            roles: [
                                ROLE_USER_CODE.SYSTEM_ADMIN,
                                ROLE_USER_CODE.BEST_BIKE_ADMIN
                            ]
                        },
                        {
                            href: ROUTES.commissionManagement.href,
                            label: ROUTES.commissionManagement.menuLabel,
                            active: pathname.includes(ROUTES.commissionManagement.href),
                            roles: [
                                ROLE_USER_CODE.SYSTEM_ADMIN,
                                ROLE_USER_CODE.BEST_BIKE_ADMIN,
                                ROLE_USER_CODE.FRANCHISE_OWNER_PRM,
                                ROLE_USER_CODE.FRANCHISE_OWNER_STD,
                                ROLE_USER_CODE.HOTEL_BIKE_OWNER,
                                ROLE_USER_CODE.SHARE_BIKE_OWNER
                            ]
                        }
                    ]
                },
                {
                    href: ROUTES.couponSetting.href,
                    label: ROUTES.couponSetting.menuLabel,
                    active: pathname.includes(ROUTES.couponSetting.href),
                    icon: BadgePercentIcon,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: []
                },
                {
                    href: ROUTES.approveManagement.href,
                    label: ROUTES.approveManagement.menuLabel,
                    active: false,
                    icon: CircleCheckBig,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: [
                        {
                            href: ROUTES.approveBikes.href,
                            label: ROUTES.approveBikes.menuLabel,
                            active: pathname.includes(ROUTES.approveBikes.href)
                        }
                    ]
                },
                {
                    href: ROUTES.otherSetting.href,
                    label: ROUTES.otherSetting.menuLabel,
                    active: false,
                    icon: Settings,
                    roles: [ROLE_USER_CODE.SYSTEM_ADMIN, ROLE_USER_CODE.BEST_BIKE_ADMIN],
                    submenus: isEk ?
                        [
                            {
                                href: ROUTES.systemSetting.href,
                                label: ROUTES.systemSetting.menuLabel,
                                active: pathname.includes(ROUTES.systemSetting.href)
                            }
                        ] :
                        [
                            {
                                href: ROUTES.kilometerLimitManagement.href,
                                label: ROUTES.kilometerLimitManagement.menuLabel,
                                active: pathname.includes(ROUTES.kilometerLimitManagement.href)
                            },
                            {
                                href: ROUTES.systemSetting.href,
                                label: ROUTES.systemSetting.menuLabel,
                                active: pathname.includes(ROUTES.systemSetting.href)
                            }
                        ]
                }
            ]
                ?.map((menu: Menu) => {
                    const hasMenuPermission = hasPermission(menu.roles, userRole);

                    const updatedSubmenus = menu.submenus.map((submenu: Submenu) => ({
                        ...submenu,
                        roles: (submenu?.roles && submenu?.roles.length > 0) ? submenu.roles : menu.roles
                    }));

                    const filteredSubmenus = updatedSubmenus.filter(submenu =>
                        hasPermission(submenu?.roles, userRole));

                    if (hasMenuPermission || filteredSubmenus.length > 0) {
                        return { ...menu, submenus: filteredSubmenus };
                    }
                    return null;
                })
                .filter(Boolean) as Menu[]
            // ?.filter((menu: Menu) => hasPermission(menu?.roles, userRole))
        }
    ];
};

export const getMenuListEU = (pathname: string, userRole: RoleUserType | undefined, t: TFunction): Menu[] => {
    // let myPageHrf: string = '';
    const isEk = isPathEk(pathname);

    if (userRole) {
        // myPageHrf = `${isEk ? '/' + ekDomain : ''}${ROUTES.myAccount.href}`;
    }

    let menu: Menu[] = [
        {
            href: `${URL_CONFIG.DOMAIN_WORDPRESS.url}/${isEk ? ekDomain : ''}`,
            label: t('words_title.home_page'),
            decoration: 'HOME',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: `/${isEk ? ekDomain + '/' : ''}${ROUTES.locationCurrentList.href}`,
            label: t('words_title.search_by_nearest_location'),
            decoration: 'SEARCH',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: `/${isEk ? ekDomain + '/' : ''}${ROUTES.locationList.href}`,
            label: t('words_title.search_by_province_region'),
            decoration: 'SEARCH',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: `${URL_CONFIG.DOMAIN_WORDPRESS.url}/${WORDPRESS_PATH.howTo[isEk ? 'hrefEk' : 'href']}`,
            label: t('words_title.how_to_use'),
            decoration: 'FLOW',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: isEk ? `/${ekDomain}/${ROUTES.priceList.hrefEK}` : `${ROUTES.priceList.href}`,
            label: t('words_title.price_list'),
            decoration: 'FEE',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: `${URL_CONFIG.DOMAIN_WORDPRESS.url}/${isEk ? ekDomain : ''}`,
            label: t('words_title.standing_moss_security_system'),
            decoration: 'SYSTEM',
            active: false,
            roles: [],
            submenus: []
        },
        {
            href: `${URL_CONFIG.DOMAIN_WORDPRESS.url}/${WORDPRESS_PATH.contact[isEk ? 'hrefEk' : 'href']}`,
            label: t('words_title.inquiry'),
            decoration: 'CONTACT',
            active: false,
            roles: [],
            submenus: []
        }
    ];

    // if (myPageHrf !== '') {
    //     menu.push({
    //         href: myPageHrf,
    //         label: t('words_title.my_page'),
    //         decoration: 'MY PAGE',
    //         active: false,
    //         roles: [],
    //         submenus: []
    //     });
    // }

    return menu;
};
// #endregion

// #region Check path GAS, EK
export const isPathEk = (pathname: string): boolean => {
    if (pathname.includes(`/${ekDomain}`)) {
        return true;
    }
    return false;
};
// #endregion
